import { parseComplexCSV } from '@utils/csvParser.js';
import { downloadFile } from '@utils/fileHandler.js';
import { generateShoplineXlsxFromShopify } from '@utils/shoplineXlsxGenerator.js';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function getCell(row, keys) {
  if (!row || !Array.isArray(keys)) {return '';}
  for (const key of keys) {
    const value = row[key];
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

function extractUrls(value) {
  if (!value) {return [];}
  const str = String(value).trim();
  if (!str) {return [];}
  const urls = str.match(/https?:\/\/[^\s,]+/g);
  if (urls?.length) {
    return urls;
  }
  return str.split(/[,\n]+/).map(v => v.trim()).filter(Boolean);
}

export function shopifyToShoplineXlsxConverter() {
  return {
    uploadedFile: null,
    fileName: null,
    fileSize: null,
    isDragging: false,

    parsedData: [],
    headers: [],
    fieldMappings: [],
    showAllMappings: false,

    stats: {
      imageCount: 0,
      mappedFieldCount: 0,
      productCount: 0,
      variantCount: 0
    },

    processing: false,
    processingProgress: 0,
    processingStage: '',

    exportReady: false,
    exportFileName: '',
    xlsxArrayBuffer: null,

    steps: [
      { id: 'upload-source', label: 'Upload Shopify CSV', status: 'pending', subtitle: 'Waiting for file' },
      { id: 'map-fields', label: 'Map Fields', status: 'pending', subtitle: '' },
      { id: 'preview', label: 'Preview Data', status: 'pending', subtitle: '' },
      { id: 'export', label: 'Export Shopline XLSX', status: 'pending', subtitle: '' }
    ],

    initDropZone() {
      const hasFiles = (event) =>
        event?.dataTransfer && [...event.dataTransfer.types || []].includes('Files');

      const handleWindowDragOver = (event) => {
        if (!hasFiles(event)) {return;}
        event.preventDefault();
        this.isDragging = true;
      };

      const handleWindowDragLeave = (event) => {
        if (!hasFiles(event)) {return;}
        if (event.relatedTarget === null) {
          this.isDragging = false;
        }
      };

      const handleWindowDrop = (event) => {
        if (!hasFiles(event)) {return;}
        event.preventDefault();
        this.isDragging = false;
      };

      window.addEventListener('dragover', handleWindowDragOver);
      window.addEventListener('dragleave', handleWindowDragLeave);
      window.addEventListener('drop', handleWindowDrop);

      return () => {
        window.removeEventListener('dragover', handleWindowDragOver);
        window.removeEventListener('dragleave', handleWindowDragLeave);
        window.removeEventListener('drop', handleWindowDrop);
      };
    },

    handleFileSelect(event) {
      this.processFile(event.target.files[0]);
    },

    handleDrop(event) {
      this.isDragging = false;
      this.processFile(event.dataTransfer.files[0]);
    },

    async processFile(file) {
      if (!file) {
        alert('Please select a file.');
        return;
      }

      if (!file.name.toLowerCase().endsWith('.csv')) {
        alert('Please upload a Shopify CSV file.');
        return;
      }

      this.uploadedFile = file;
      this.fileName = file.name;
      this.fileSize = this.formatFileSize(file.size);
      this.processing = true;
      this.processingProgress = 0;
      this.processingStage = 'Reading file...';

      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          this.processingProgress = 20;
          this.processingStage = 'Detecting CSV format...';
          await new Promise(resolve => setTimeout(resolve, 60));

          const result = parseComplexCSV(csvText);

          if (result.csvFormat !== 'shopify') {
            throw new Error('This file is not a Shopify CSV export. Please upload a Shopify product export file.');
          }

          this.parsedData = result.parsedData;
          this.headers = result.headers;

          this.processingProgress = 45;
          this.processingStage = 'Mapping fields...';
          await new Promise(resolve => setTimeout(resolve, 60));
          this.generateFieldMappings();

          this.processingProgress = 65;
          this.processingStage = 'Calculating statistics...';
          await new Promise(resolve => setTimeout(resolve, 60));
          this.calculateStats();

          this.processingProgress = 85;
          this.processingStage = 'Generating Shopline XLSX...';
          await new Promise(resolve => setTimeout(resolve, 60));
          this.generateShoplineXlsx();

          this.processingProgress = 100;
          this.processingStage = 'Complete!';
          await new Promise(resolve => setTimeout(resolve, 200));

          this.exportReady = true;
          this.exportFileName = `shopline_import_ready_${new Date().toISOString().split('T')[0]}.xlsx`;

          this.steps[0].status = 'completed';
          this.steps[0].subtitle = 'Shopify CSV uploaded';
          this.steps[1].status = 'completed';
          this.steps[1].subtitle = `${this.stats.mappedFieldCount} fields mapped`;
          this.steps[2].status = 'completed';
          this.steps[2].subtitle = `${this.stats.productCount} products, ${this.stats.variantCount} variants`;
          this.steps[3].status = 'completed';
          this.steps[3].subtitle = 'Ready to download';

          this.processing = false;
          this.processingProgress = 0;
          this.processingStage = '';
        } catch (error) {
          console.error('Error processing file:', error);
          this.processing = false;
          this.processingProgress = 0;
          this.processingStage = '';
          alert(`Error processing file: ${error.message}`);
        }
      };

      reader.onerror = () => {
        this.processing = false;
        this.processingProgress = 0;
        this.processingStage = '';
        alert('Failed to read file. Please try again.');
      };

      reader.readAsText(file);
    },

    generateFieldMappings() {
      const product = this.parsedData[0] || {};
      const variants = [product, ...(Array.isArray(product.variants) ? product.variants : [])];
      const variant = variants[0] || {};

      const mappings = [
        {
          source: 'Handle',
          target: 'Product Handle*',
          sample: getCell(product, ['Handle'])
        },
        {
          source: 'Title',
          target: 'Product Name(English)*',
          sample: getCell(product, ['Title'])
        },
        {
          source: 'Body (HTML)',
          target: 'Product Description(English)',
          sample: this.truncateValue(getCell(product, ['Body (HTML)']), 60)
        },
        {
          source: 'Vendor',
          target: 'Supplier',
          sample: getCell(product, ['Vendor'])
        },
        {
          source: 'Type / Product Category',
          target: 'Online Store Categories(English)',
          sample: getCell(product, ['Type', 'Product Category', 'Product category'])
        },
        {
          source: 'Tags',
          target: 'Product Tag',
          sample: this.truncateValue(getCell(product, ['Tags']), 60)
        },
        {
          source: 'Image Src + Variant Image',
          target: 'Images* / Additional Images / Variation Image',
          sample: getCell(product, ['Image Src', 'Product image URL']) || getCell(variant, ['Variant Image', 'Variant image URL'])
        },
        {
          source: 'Published + Status',
          target: 'Online Store Status / Hidden Product',
          sample: `${getCell(product, ['Published', 'Published on online store']) || '-'} / ${getCell(product, ['Status']) || '-'}`
        },
        {
          source: 'Option1/2 Name + Option1/2 Value',
          target: 'Specification Name A/B + Variation name A/B',
          sample: `${getCell(variant, ['Option1 Value']) || '-'} | ${getCell(variant, ['Option2 Value']) || '-'}`
        },
        {
          source: 'Variant Price + Variant Compare At Price',
          target: 'Variation price + Variant Sale Price',
          sample: `${getCell(variant, ['Variant Price']) || '-'} / ${getCell(variant, ['Variant Compare At Price']) || '-'}`
        },
        {
          source: 'Variant Inventory Qty',
          target: 'Variation quantity',
          sample: getCell(variant, ['Variant Inventory Qty', 'Inventory quantity'])
        },
        {
          source: 'Variant SKU',
          target: 'Variation SKU',
          sample: getCell(variant, ['Variant SKU'])
        },
        {
          source: 'Variant Weight / Variant Grams',
          target: 'Variant Weight(KG)',
          sample: getCell(variant, ['Variant Weight', 'Variant Grams', 'Weight value (grams)'])
        },
        {
          source: 'SEO Title / SEO Description',
          target: 'SEO Title(English) / SEO Description(English)',
          sample: `${getCell(product, ['SEO Title']) || '-'}`
        }
      ];

      this.fieldMappings = mappings;
      this.stats.mappedFieldCount = mappings.length;
    },

    calculateStats() {
      const productCount = this.parsedData.length;
      let variantCount = 0;
      const imageSet = new Set();

      this.parsedData.forEach((product) => {
        const variants = [product, ...(Array.isArray(product.variants) ? product.variants : [])];
        variantCount += variants.length;

        const productImages = getCell(product, ['Image Src', 'Product image URL']);
        extractUrls(productImages).forEach(url => imageSet.add(url));

        variants.forEach((variant) => {
          extractUrls(getCell(variant, ['Image Src', 'Product image URL', 'Variant Image', 'Variant image URL']))
            .forEach(url => imageSet.add(url));
        });
      });

      this.stats.productCount = productCount;
      this.stats.variantCount = variantCount;
      this.stats.imageCount = imageSet.size;
    },

    generateShoplineXlsx() {
      const { arrayBuffer } = generateShoplineXlsxFromShopify(this.parsedData);
      this.xlsxArrayBuffer = arrayBuffer;
    },

    downloadShoplineXlsx() {
      if (!this.xlsxArrayBuffer) {
        alert('No XLSX data available. Please upload and process a file first.');
        return;
      }

      downloadFile(this.xlsxArrayBuffer, this.exportFileName, XLSX_MIME);
    },

    formatNumber(num) {
      return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    formatFileSize(bytes) {
      if (bytes < 1024) {return bytes + ' B';}
      if (bytes < 1024 * 1024) {return (bytes / 1024).toFixed(1) + ' KB';}
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    truncateValue(value, maxLength) {
      if (!value) {return '';}
      if (value.length <= maxLength) {return value;}
      return value.slice(0, maxLength) + '...';
    },

    reset() {
      this.uploadedFile = null;
      this.fileName = null;
      this.fileSize = null;
      this.parsedData = [];
      this.headers = [];
      this.fieldMappings = [];
      this.showAllMappings = false;
      this.stats = {
        imageCount: 0,
        mappedFieldCount: 0,
        productCount: 0,
        variantCount: 0
      };
      this.processing = false;
      this.processingProgress = 0;
      this.processingStage = '';
      this.exportReady = false;
      this.exportFileName = '';
      this.xlsxArrayBuffer = null;

      this.steps.forEach((step, index) => {
        if (index === 0) {
          step.status = 'pending';
          step.subtitle = 'Waiting for file';
        } else {
          step.status = 'pending';
          step.subtitle = '';
        }
      });

      const fileInput = this.$refs.convertFileInput;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };
}
